import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, CATEGORIES, CITIES, EVENT_COST, URGENT_COST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap } from "lucide-react";

const COMP_UNITS = [["ora", "€ / ora"], ["giorno", "€ / giorno"], ["da_concordare", "Da concordare"]];

const Field = ({ label, className = "", ...props }) => (
  <label className={`block ${className}`}>
    <span className="eyebrow !text-neutral-500">{label}</span>
    <input className="lux-input [color-scheme:dark]" {...props} />
  </label>
);

export default function CreateEvent() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ title: "", city: "", location: "", date: "", start_time: "", end_time: "", compensation: "", compensation_unit: "giorno", hostess_needed: 1, category: "Hostess", description: "", urgent: false });
  const total = EVENT_COST + (form.urgent ? URGENT_COST : 0);
  const enough = (user.credits ?? 0) >= total;
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const negotiable = form.compensation_unit === "da_concordare";
      const r = await api.post("/events", { ...form, compensation: !negotiable && form.compensation ? Number(form.compensation) : null, compensation_unit: negotiable ? null : form.compensation_unit, hostess_needed: Number(form.hostess_needed) });
      toast.success("Evento pubblicato. Riceverai una notifica per ogni hostess disponibile.");
      refresh();
      nav(`/eventi/${r.data.id}`);
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoading(false); }
  };

  return (
    <main className="max-w-4xl mx-auto px-6 lg:px-10 pt-16" data-testid="create-event-page">
      <p className="eyebrow">Nuovo evento</p>
      <h1 className="font-serif text-4xl sm:text-5xl mt-3">Di chi hai bisogno?</h1>
      <p className="text-neutral-400 mt-4 max-w-xl">Pubblica il tuo evento: le hostess interessate si candideranno e riceverai una notifica in tempo reale per ogni disponibilità.</p>
      <form onSubmit={submit} className="mt-14 grid sm:grid-cols-2 gap-x-10 gap-y-8" data-testid="create-event-form">
        <Field label="Titolo evento" className="sm:col-span-2" value={form.title} onChange={set("title")} required placeholder="Es. Lancio prodotto – Showroom Brera" data-testid="event-title-input" />
        <Field label="Città" value={form.city} onChange={set("city")} required list="ev-cities" data-testid="event-city-input" />
        <datalist id="ev-cities">{CITIES.map((c) => <option key={c} value={c} />)}</datalist>
        <Field label="Location" value={form.location} onChange={set("location")} placeholder="Indirizzo o venue" data-testid="event-location-input" />
        <Field label="Data" type="date" value={form.date} onChange={set("date")} required data-testid="event-date-input" />
        <div className="grid grid-cols-2 gap-6">
          <Field label="Inizio" type="time" value={form.start_time} onChange={set("start_time")} data-testid="event-start-input" />
          <Field label="Fine" type="time" value={form.end_time} onChange={set("end_time")} data-testid="event-end-input" />
        </div>
        <div>
          <Field label="Compenso" type="number" min={1} value={form.compensation} onChange={set("compensation")} disabled={form.compensation_unit === "da_concordare"} placeholder={form.compensation_unit === "da_concordare" ? "Da concordare" : "Importo in €"} required={form.compensation_unit !== "da_concordare"} className="[&_input:disabled]:opacity-40" data-testid="event-compensation-input" />
          <div className="flex flex-wrap gap-2 mt-3" data-testid="event-compensation-unit">
            {COMP_UNITS.map(([v, l]) => (
              <button type="button" key={v} onClick={() => setForm({ ...form, compensation_unit: v, compensation: v === "da_concordare" ? "" : form.compensation })} data-testid={`event-compensation-unit-${v}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] border transition-colors ${form.compensation_unit === v ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-neutral-400 hover:border-white/30"}`}>{l}</button>
            ))}
          </div>
        </div>
        <Field label="Numero hostess" type="number" min={1} value={form.hostess_needed} onChange={set("hostess_needed")} data-testid="event-needed-input" />
        <div className="sm:col-span-2">
          <span className="eyebrow !text-neutral-500">Profilo richiesto</span>
          <div className="flex flex-wrap gap-2 mt-3">
            {CATEGORIES.map((c) => (
              <button type="button" key={c} onClick={() => setForm({ ...form, category: c })} data-testid={`event-category-${c}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.15em] border transition-colors ${form.category === c ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-neutral-400 hover:border-white/30"}`}>{c}</button>
            ))}
          </div>
        </div>
        <label className="block sm:col-span-2">
          <span className="eyebrow !text-neutral-500">Descrizione e requisiti</span>
          <textarea rows={4} className="lux-input resize-none" value={form.description} onChange={set("description")} placeholder="Dress code, lingue richieste, mansioni…" data-testid="event-description-input" />
          <span className="block text-xs text-neutral-400 mt-2" data-testid="event-description-hint">Non inserire contatti (telefono, email, link o social): le hostess ti contatteranno tramite la piattaforma.</span>
        </label>
        <button type="button" onClick={() => setForm({ ...form, urgent: !form.urgent })} aria-pressed={form.urgent} data-testid="event-urgent-toggle"
          className={`sm:col-span-2 text-left p-5 border flex items-start gap-4 transition-colors ${form.urgent ? "border-[#E5484D]/60 bg-[#E5484D]/5" : "border-white/10 hover:border-white/30"}`}>
          <Zap className={`w-5 h-5 mt-0.5 ${form.urgent ? "text-[#E5484D] fill-current" : "text-neutral-500"}`} />
          <span className="flex-1">
            <span className="block text-sm text-neutral-100">Evento urgente <span className="font-mono text-xs text-neutral-400">+{URGENT_COST} crediti</span></span>
            <span className="block text-xs text-neutral-400 mt-1">Badge "Urgente" e posizione in cima alla bacheca eventi fino alla data dell'evento.</span>
          </span>
          <span className={`w-4 h-4 mt-1 border ${form.urgent ? "bg-[#E5484D] border-[#E5484D]" : "border-white/30"}`} />
        </button>
        <div className="sm:col-span-2 flex flex-wrap items-center gap-6">
          <button disabled={loading || !enough} className="btn-gold" data-testid="event-submit-button">{loading ? "Pubblicazione…" : `Pubblica evento · ${total} crediti`}</button>
          <span className="text-sm text-neutral-500" data-testid="event-credits-info">
            Saldo: <span className="text-white font-mono">{user.credits ?? 0}</span> crediti
            {!enough && <> · <Link to="/crediti" className="text-[#D4AF37] hover:underline" data-testid="event-buy-credits-link">Acquista crediti</Link></>}
          </span>
        </div>
      </form>
    </main>
  );
}
